/**
 * Gemeinsame Typen der Fachlogik.
 *
 * Konventionen:
 * - Datumswerte sind ISO-Strings 'YYYY-MM-DD' (Kalendertage ohne Uhrzeit).
 * - Monatsschlüssel sind 'YYYY-MM'.
 * - Geld ist immer eine ganze Zahl in Cent (bzw. Rappen/Pence).
 * - Prozentsätze sind Zahlen in Prozent (z. B. 19 oder 8.1).
 * - Nichts hier kennt eine konkrete Agentur: agenturspezifische Werte kommen
 *   über OrgSettings, DunningPolicy, TaxConfig usw. herein.
 */

export type IsoDate = string;
export type MonthKey = string;
export type Cents = number;

export type Currency = "EUR" | "CHF" | "USD" | "GBP" | "SEK" | "DKK";
/** Gleich halten mit der Datenbank-Domäne public.currency_code (0001). */
export const CURRENCIES: readonly Currency[] = ["EUR", "CHF", "USD", "GBP", "SEK", "DKK"];

export type MoneyLocale = "de-DE" | "de-AT" | "de-CH" | "en-GB";
export type Language = "de" | "en";

/** ISO-3166-1 alpha-2, groß geschrieben. */
export type CountryCode = string;

/** Steuerzone eines Landes (WS-01): DACH je eigen, übrige EU, Drittland, unbekannt. */
export type TaxZone = "DE" | "AT" | "CH" | "EU" | "NON_EU" | "UNKNOWN";

/** Prüfstatus einer Rechnung (neutral benannt; „processed“ heißt hier „approved“). */
export type ReviewStatus = "not_submitted" | "submitted" | "approved" | "rejected" | "revision";

/** Zahlstatus einer Rechnung. */
export type PaymentStatus = "open" | "paid" | "void" | "cancelled" | "deleted";

/** Antwort des Talents auf „Ist das Geld da?“. */
export type TalentPaymentResponse = "paid" | "still_overdue";

/** Reservierte Mahnstufen-Schlüssel. Alle übrigen Stufen kommen aus der DunningPolicy. */
export const DUNNING_NONE = "none";
export const DUNNING_COLLECTION = "collection";

export type DeadlineUnit = "working" | "calendar";

export interface DunningStageConfig {
  /** frei wählbarer Schlüssel, z. B. 'reminder', 'first', 'second', 'final' */
  key: string;
  /** Frist, die mit dem Schreiben gesetzt wird */
  deadline: { days: number; unit: DeadlineUnit };
  /** Mahngebühr dieser Stufe in Cent der Policy-Währung */
  feeCents: Cents;
  /** Verzugszinsen auf dieser Stufe berechnen */
  interest: boolean;
  /** Zahlungserinnerung: darf vor/zur Fälligkeit ohne Rückfrage raus (MA-07) */
  isReminder?: boolean;
  /** Eigene Bezeichnung der Stufe aus der Mahnleiter (dunning_steps.label, 0109); geht jedem Standardnamen vor */
  label?: string | null;
}

export interface BaseRatePeriod {
  validFrom: IsoDate;
  percent: number;
}

export interface DunningPolicy {
  stages: DunningStageConfig[];
  /** Währung, in der die Gebühren gepflegt sind */
  feeCurrency: Currency;
  /** 'highest' = Gebühr einmalig (höchste erreichte Stufe), 'cumulative' = je Stufe addiert */
  feeMode: "highest" | "cumulative";
  /** Basiszinssatz mit Gültigkeit ab (halbjährlich pflegbar) */
  baseRates: BaseRatePeriod[];
  /** Aufschlag in Prozentpunkten (z. B. B2B) */
  surchargePercentPoints: number;
  /** einmalige Pauschale in Cent der Policy-Währung; fällt genau einmal bei der ersten Stufe an, die keine Erinnerung ist */
  flatFeeCents?: Cents;
  /**
   * Mahnung im Auftrag des Creators (Creator-Rechnung an die Marke, 0161): Gebühren, Pauschale (Deutschland § 288 Abs. 5 BGB,
   * Österreich § 458 UGB) und Verzugszinsen auch für den Creator geltend machen. Ohne Angabe an (die Ansprüche stehen dem Creator
   * zu). Hat der Creator seinen Sitz in einem anderen Land als die Agentur, rechnet CreatorOffice Pauschale und Zinsen nicht
   * automatisch (0181, computeDunningCosts mit ruleCountry).
   */
  creatorClaimsCosts?: boolean;
  /**
   * CH-2 (Grundsatz 15.10.2026, A1): vertraglich vereinbarter fester Zinssatz in Prozent pro Jahr. Ersetzt Basiszins plus Aufschlag
   * bei Geschäften zwischen Unternehmen; ohne Angabe gilt die Regel des Landes.
   */
  contractRatePercent?: number | null;
  /**
   * CH-2 (A2, K-281): ab wann Verzugszinsen laufen: „due“ = ab Fälligkeit (DE/AT, Schweiz mit vereinbartem Verfalltag),
   * „reminder“ = ab der ersten Mahnung (Schweiz ohne Verfalltag, Art. 102 OR). Ohne Angabe ab Fälligkeit.
   */
  interestStart?: "due" | "reminder";
}

/** Einstellungen einer Agentur (Mandant), soweit die Fachlogik sie braucht. */
export interface OrgSettings {
  /** IANA-Zeitzone für Monats- und Tagesgrenzen */
  timeZone: string;
  /** Sprache für Rechnungen an die eigene Firma */
  ownOrgLanguage: Language;
  /** erlaubte Rechnungswährungen */
  currencies: Currency[];
  /** Agentur-Standard-Provisionssatz in Prozent (Agenturanteil) */
  defaultCommissionPercent: number;
  /** Standard-Zahlungsfrist in Tagen */
  defaultPaymentTermDays: number;
  /** „fällig in X T“-Fenster */
  dueSoonDays: number;
  /** Abkühlzeit zwischen zwei Nachfragen beim Talent */
  paymentCheckCooldownDays: number;
  /** Nach „noch nicht angekommen“ wird „Erneut nachfragen“ nach so vielen Tagen Hauptknopf */
  stillOverdueRecheckDays: number;
  /** Nach Zahlungsbestätigung der Agentur ohne Talent-Antwort: nach so vielen Tagen wieder Handlungsbedarf */
  talentAnswerDays: number;
  /** Mindestlänge einer Leistungsbeschreibung bei bepreisten Positionen */
  minLineDescriptionChars: number;
  /** Rechnungsdatum nur an Werktagen */
  invoiceDateWorkdaysOnly: boolean;
  /** Abweichung Positionssumme ↔ Deal-Wert, ab der ein Hinweis kommt (Prozent) */
  budgetDeviationPercent: number;
  /** Stichworte, die auf eine provisionsfreie Auslage hindeuten */
  expenseKeywords: string[];
  /** Laufzeit von Signierlinks in Tagen (WS-14) */
  signingLinkDays: number;
  /** Nummernschema-Prüfung (NR-06) */
  invoiceNumberScheme: InvoiceNumberScheme;
}

export interface InvoiceNumberScheme {
  /**
   * Monat in der Nummer ↔ Rechnungsdatum (G-01, 0105): 'off' = keine Prüfung (Produkt-Standard), 'detect' = Monatsangaben
   * erkennen und bei Abweichung nachfragen (Rückfrage, kein Fehler), 'strict' = bei Abweichung sperren (nur, wenn die Agentur
   * es ausdrücklich so einstellt).
   */
  monthCheck: "off" | "detect" | "strict";
  /**
   * Optionales festes Muster, z. B. '{P}-{YYYY}-{MM}-{N}'. Tokens: {YYYY} Jahr, {MM} Monat (2-stellig),
   * {N} laufende Nummer, {P} beliebiges Präfix. Ist es gesetzt, wird der Monat nur daraus gelesen.
   */
  template?: string;
}

/** Rechte eines Handelnden an einer Rechnung (G-06). */
export interface ActorPermissions {
  role: "agency" | "talent";
  /** Talent: gehört die Rechnung diesem Talent? */
  isOwnInvoice?: boolean;
  canApproveInvoices?: boolean;
  canSendToBrand?: boolean;
  canDun?: boolean;
  canConfirmPayment?: boolean;
  canTriggerPayout?: boolean;
}

/** Rohdatensatz einer Rechnung, wie ihn die Fachlogik braucht. */
export interface InvoiceRecord {
  id: string;
  number: string | null;
  /** Aussteller (Talent) */
  issuerId: string;
  dealId: string | null;
  /** Leistungsmonat 'YYYY-MM' */
  revenueMonth: MonthKey | null;
  reviewStatus: ReviewStatus;
  status: PaymentStatus;
  hasPdf: boolean;
  amountGrossCents: Cents;
  currency: Currency;
  invoiceDate: IsoDate | null;
  serviceDate: IsoDate | null;
  /** Fälligkeit, die der Aussteller beim Erzeugen berechnet hat (vor dem Versand maßgeblich) */
  issuerDueDate: IsoDate | null;
  /** Fälligkeit auf dem an den Empfänger versendeten Beleg; wird beim Erstversand festgelegt (WS-10) */
  documentDueDate: IsoDate | null;
  /** aktuell geltende Fälligkeit, nur durch ausdrückliche Verlängerung verschoben (WS-10/WS-11) */
  effectiveDueDate: IsoDate | null;
  submittedAt: IsoDate | null;
  adminReviewedAt: IsoDate | null;
  rejectionReason: string | null;
  rejectionMailSentAt: IsoDate | null;
  sentToBrandAt: IsoDate | null;
  sentToBrandCount: number;
  lastSentToBrandAt: IsoDate | null;
  /** DUNNING_NONE, ein Stufen-Schlüssel der Policy oder DUNNING_COLLECTION */
  dunningStage: string;
  lastDunningAt: IsoDate | null;
  dunningDeadline: IsoDate | null;
  dunningBlocked: boolean;
  collectionAt: IsoDate | null;
  cancelledAt: IsoDate | null;
  paidAt: IsoDate | null;
  /** Nachweis der Zahlung (WS-09) */
  paymentProofRef: string | null;
  talentPaymentResponse: TalentPaymentResponse | null;
  talentPaymentResponseAt: IsoDate | null;
  overdueCheckRequestedAt: IsoDate | null;
  adminPaymentConfirmedAt: IsoDate | null;
  discardedAt: IsoDate | null;
  /** Rechnung zurückgezogen (nie wirksam geworden) – gibt die Nummer frei (WS-06) */
  withdrawnAt: IsoDate | null;
  /** Empfänger ist die eigene Agentur; die Aufrufer bestimmen das aus IDs (WS-17) */
  recipientIsOwnOrg: boolean;
  /** Rechnungsart (fehlt nur in alten Testdaten; dann gilt die Rechnung als Creator-Rechnung) */
  kind?: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  /** Stornorechnung zu dieser Rechnung (0043): abgeschlossen nach dem Versand, nie gemahnt */
  creditFor?: string | null;
  /** bisher eingegangener Betrag (Teilzahlungen, 0043) */
  paidCents?: Cents;
  /** Gutschrift: die Agentur hat für den Creator abgerechnet (0046); wird storniert, nie zurückgegeben */
  selfBilled?: boolean;
  /**
   * Zugang der Rechnung beim Empfänger (0055): Tag der ersten zugestellten Rechnungsmail bzw. vermerkten anderen Zustellung.
   * null = versendet, aber (noch) nicht zugegangen; undefined = unbekannt (alte Datensätze, gilt als zugegangen).
   */
  deliveredAt?: IsoDate | null;
  /** Letzte gescheiterte Rechnungsmail ohne spätere Zustellung (0055) */
  deliveryFailedAt?: IsoDate | null;
  /** Forderung als uneinbringlich ausgebucht (0055) */
  badDebtAt?: IsoDate | null;
  /** Die Marke hat an den Creator statt an die Agentur gezahlt (0055) */
  paidViaCreatorAt?: IsoDate | null;
  /**
   * Fälligkeit der Auszahlung an den Creator (Rechnung an die Agentur, 0055): nach Auszahlungsmodus, im Modus „nach
   * Zahlungseingang“ erst ab Zahlung der Marke. null = noch keine (Marke hat nicht gezahlt); undefined = Fälligkeit der Rechnung.
   */
  payoutDueDate?: IsoDate | null;
}

/** Leerer Rechnungsdatensatz als Ausgangspunkt (für Aufrufer und Tests). */
export function emptyInvoice(id: string, issuerId: string): InvoiceRecord {
  return {
    id,
    number: null,
    issuerId,
    dealId: null,
    revenueMonth: null,
    reviewStatus: "not_submitted",
    status: "open",
    hasPdf: false,
    amountGrossCents: 0,
    currency: "EUR",
    invoiceDate: null,
    serviceDate: null,
    issuerDueDate: null,
    documentDueDate: null,
    effectiveDueDate: null,
    submittedAt: null,
    adminReviewedAt: null,
    rejectionReason: null,
    rejectionMailSentAt: null,
    sentToBrandAt: null,
    sentToBrandCount: 0,
    lastSentToBrandAt: null,
    dunningStage: DUNNING_NONE,
    lastDunningAt: null,
    dunningDeadline: null,
    dunningBlocked: false,
    collectionAt: null,
    cancelledAt: null,
    paidAt: null,
    paymentProofRef: null,
    talentPaymentResponse: null,
    talentPaymentResponseAt: null,
    overdueCheckRequestedAt: null,
    adminPaymentConfirmedAt: null,
    discardedAt: null,
    withdrawnAt: null,
    recipientIsOwnOrg: false,
  };
}

/** Ergebnis einer Prüfung, die nie blockiert (Hinweis/Rückfrage). */
export interface Finding {
  code: string;
  severity: "error" | "warning" | "hint" | "question";
  params?: Record<string, string | number>;
}

/** Wirft bei Programmierfehlern (nicht bei fachlichen Befunden). */
export class DomainError extends Error {
  constructor(public readonly code: string, message?: string) {
    super(message ? `${code}: ${message}` : code);
    this.name = "DomainError";
  }
}
